import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { t } from '@/lib/i18n';
import { InlineEntityList } from '../InlineEntityList';
import type { InlineFieldDef } from '../InlineEntityList';
import {
  useRorosIcps,
  useRorosIcpCreate,
  useRorosIcpUpdate,
  useRorosIcpRemove,
  useRorosIcpReorder,
} from '@/hooks/roros/overarching';
import type { RorosIcpDTO } from '@/types/roros/overarching';
import { useOverarchingEditable } from '@/routes/roros/overarching/OverarchingEditableContext';
import { PersonaLinkList } from './PersonaLinkList';

// ─── ICPs under a Market ─────────────────────────────────────────────────────

export function IcpSubtree({ marketId }: { marketId: string }) {
  const icpsQuery = useRorosIcps({ targetMarketId: marketId });
  const createIcp = useRorosIcpCreate();
  const updateIcp = useRorosIcpUpdate();
  const removeIcp = useRorosIcpRemove();
  const reorderIcps = useRorosIcpReorder();
  const { canEdit } = useOverarchingEditable();

  const icps = (icpsQuery.data ?? []) as RorosIcpDTO[];

  const fields: InlineFieldDef<RorosIcpDTO>[] = [
    { name: 'name', label: t('Name'), type: 'text', required: true, placeholder: t('e.g. Mid-market SaaS') },
    { name: 'description', label: t('Description'), type: 'textarea', placeholder: t('Who is this ICP?') },
    { name: 'firmographics', label: t('Firmographics'), type: 'textarea', placeholder: t('Size, industry, region...') },
    { name: 'buyingTriggers', label: t('Buying triggers'), type: 'textarea', placeholder: t('What makes them buy?') },
    { name: 'budgetRange', label: t('Budget range'), type: 'text', placeholder: t('e.g. €50k-€500k') },
  ];

  async function handleReorder(index: number, direction: 'up' | 'down') {
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    await reorderIcps.mutateAsync([
      { id: icps[index].id, sortOrder: icps[swapIdx].sortOrder },
      { id: icps[swapIdx].id, sortOrder: icps[index].sortOrder },
    ]);
  }

  return (
    <div className="ml-6 border-l-2 border-border pl-4">
      <InlineEntityList<RorosIcpDTO>
        title={t('ICPs')}
        emptyHint={t('No ICPs under this market yet.')}
        items={icps}
        fields={fields}
        addLabel={t('Add ICP')}
        readOnly={!canEdit}
        onCreate={async (draft) => {
          await createIcp.mutateAsync({ ...(draft as Record<string, unknown>), targetMarketId: marketId });
        }}
        onUpdate={async (id, patch) => { await updateIcp.mutateAsync({ id, payload: patch as Record<string, unknown> }); }}
        onDelete={async (id) => { await removeIcp.mutateAsync(id); }}
        onReorder={handleReorder}
        renderRow={(icp) => (
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{icp.name}</p>
            {icp.description && (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{icp.description}</p>
            )}
            {icp.budgetRange && (
              <Badge variant="secondary" className="text-[10px] mt-1">{icp.budgetRange}</Badge>
            )}
          </div>
        )}
        renderExpanded={(icp) => <PersonaLinkList icpId={icp.id} />}
      />
    </div>
  );
}
